import { useState, useMemo } from 'react'
import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { SEO_PAGE_HEADERS } from '~/lib/cache.server'
import { LeadCaptureBlock } from '~/components/lead-capture-block'
import { BreadcrumbSchema, StructuredData } from '~/components/structured-data'
import { ArticleSchema, HowToSchema } from '~/components/seo-content'
import {
  Calculator, Euro, ArrowRight, Leaf, Info,
} from 'lucide-react'

export function headers() { return SEO_PAGE_HEADERS }

// ── Calculator definitions ──

interface CalcField {
  id: string
  label: string
  type: 'number' | 'select' | 'range'
  unit?: string
  min?: number
  max?: number
  step?: number
  default: number | string
  options?: Array<{ value: string; label: string }>
}

interface CalcDef {
  title: string
  subtitle: string
  category: string
  fields: CalcField[]
  calculate: (v: Record<string, number | string>) => CalcResult
}

interface CalcResult {
  total: { min: number; max: number }
  breakdown: Array<{ label: string; min: number; max: number }>
  aides?: Array<{ label: string; amount: string; note?: string }>
  savings?: string
  disclaimer: string
}

const fmt = (n: number) => n.toLocaleString('fr-FR')

const CALCULATORS: Record<string, CalcDef> = {
  'renovation-m2': {
    title: 'Calculateur rénovation au m²',
    subtitle: 'Estimez le coût de votre rénovation complète',
    category: 'renovation',
    fields: [
      { id: 'surface', label: 'Surface', type: 'range', unit: 'm²', min: 10, max: 200, step: 5, default: 50 },
      { id: 'quality', label: 'Niveau de finition', type: 'select', default: 'standard', options: [
        { value: 'basique', label: 'Basique (rafraîchissement)' },
        { value: 'standard', label: 'Standard (remplacement complet)' },
        { value: 'premium', label: 'Premium (haut de gamme)' },
      ]},
      { id: 'city', label: 'Localisation', type: 'select', default: 'paris', options: [
        { value: 'paris', label: 'Paris / Île-de-France' },
        { value: 'province', label: 'Province / Grandes villes' },
      ]},
    ],
    calculate(v) {
      const s = Number(v.surface)
      const rates: Record<string, { min: number; max: number }> = {
        basique: { min: 250, max: 450 },
        standard: { min: 600, max: 1000 },
        premium: { min: 1100, max: 1800 },
      }
      const r = rates[v.quality as string] ?? rates.standard
      const mult = v.city === 'paris' ? 1.25 : 1
      const min = Math.round(s * r.min * mult)
      const max = Math.round(s * r.max * mult)
      const pct = (p: number) => ({ min: Math.round(min * p), max: Math.round(max * p) })
      return {
        total: { min, max },
        breakdown: [
          { label: 'Démolition / dépose', ...pct(0.08) },
          { label: 'Plomberie', ...pct(0.15) },
          { label: 'Électricité', ...pct(0.15) },
          { label: 'Revêtements sols et murs', ...pct(0.25) },
          { label: 'Peinture', ...pct(0.12) },
          { label: 'Finitions et divers', ...pct(0.10) },
          { label: 'Main d\'œuvre', ...pct(0.15) },
        ],
        aides: v.quality !== 'basique' ? [
          { label: 'MaPrimeRénov\' (si travaux énergétiques)', amount: '1 500 — 10 000 €', note: 'Selon revenus et type de travaux' },
          { label: 'Éco-PTZ', amount: 'Jusqu\'à 50 000 € à taux zéro', note: 'Rénovation globale' },
        ] : undefined,
        disclaimer: 'Estimation indicative basée sur les prix moyens constatés en France en 2026. Le coût réel dépend de l\'état existant, de l\'accessibilité du chantier et des matériaux choisis. Demandez des devis détaillés pour un chiffrage précis.',
      }
    },
  },
  'salle-de-bain': {
    title: 'Calculateur salle de bain',
    subtitle: 'Estimez le coût de votre nouvelle salle de bain',
    category: 'plomberie',
    fields: [
      { id: 'surface', label: 'Surface', type: 'range', unit: 'm²', min: 3, max: 20, step: 1, default: 6 },
      { id: 'douche', label: 'Douche ou baignoire', type: 'select', default: 'douche-italienne', options: [
        { value: 'douche-classique', label: 'Douche classique' },
        { value: 'douche-italienne', label: 'Douche à l\'italienne' },
        { value: 'baignoire', label: 'Baignoire' },
        { value: 'baignoire-balneo', label: 'Baignoire balnéo' },
      ]},
      { id: 'vasque', label: 'Vasque', type: 'select', default: 'simple', options: [
        { value: 'simple', label: 'Simple vasque' },
        { value: 'double', label: 'Double vasque' },
      ]},
      { id: 'wc', label: 'WC inclus', type: 'select', default: 'oui', options: [
        { value: 'non', label: 'Non' },
        { value: 'oui', label: 'WC classique' },
        { value: 'suspendu', label: 'WC suspendu' },
      ]},
      { id: 'quality', label: 'Niveau de gamme', type: 'select', default: 'standard', options: [
        { value: 'basique', label: 'Entrée de gamme' },
        { value: 'standard', label: 'Milieu de gamme' },
        { value: 'premium', label: 'Haut de gamme' },
      ]},
    ],
    calculate(v) {
      const s = Number(v.surface)
      const qualityMult: Record<string, number> = { basique: 0.7, standard: 1, premium: 1.6 }
      const qm = qualityMult[v.quality as string] ?? 1
      const douchePrix: Record<string, { min: number; max: number }> = {
        'douche-classique': { min: 800, max: 1500 },
        'douche-italienne': { min: 1500, max: 3500 },
        'baignoire': { min: 600, max: 1800 },
        'baignoire-balneo': { min: 2000, max: 5000 },
      }
      const dp = douchePrix[v.douche as string] ?? douchePrix['douche-italienne']
      const vasquePrix = v.vasque === 'double' ? { min: 800, max: 2200 } : { min: 300, max: 1200 }
      const wcPrix: Record<string, { min: number; max: number }> = {
        non: { min: 0, max: 0 },
        oui: { min: 200, max: 600 },
        suspendu: { min: 500, max: 1200 },
      }
      const wp = wcPrix[v.wc as string] ?? wcPrix.oui
      const carrelage = { min: s * 50, max: s * 130 }
      const plomberie = { min: 800, max: 2000 }
      const elec = { min: 400, max: 1000 }
      const peinture = { min: s * 15, max: s * 35 }
      const mo = { min: 1500, max: 4000 }

      const items = [
        { label: 'Douche / Baignoire', ...dp },
        { label: v.vasque === 'double' ? 'Double vasque + meuble' : 'Vasque + meuble', ...vasquePrix },
        ...(v.wc !== 'non' ? [{ label: v.wc === 'suspendu' ? 'WC suspendu + bâti' : 'WC', ...wp }] : []),
        { label: 'Carrelage sol et murs', ...carrelage },
        { label: 'Plomberie (alimentation + évacuation)', ...plomberie },
        { label: 'Électricité (mise aux normes)', ...elec },
        { label: 'Peinture plafond + finitions', ...peinture },
        { label: 'Main d\'œuvre pose', ...mo },
      ]
      const breakdown = items.map(i => ({ label: i.label, min: Math.round(i.min * qm), max: Math.round(i.max * qm) }))
      const total = { min: breakdown.reduce((s, i) => s + i.min, 0), max: breakdown.reduce((s, i) => s + i.max, 0) }
      return {
        total, breakdown,
        disclaimer: 'Estimation indicative incluant fournitures et pose. Le coût varie selon l\'état existant (dépose, remise en état murs) et la marque des équipements. Demandez 3 devis pour un chiffrage précis.',
      }
    },
  },
  'cuisine': {
    title: 'Calculateur cuisine',
    subtitle: 'Estimez le budget de votre nouvelle cuisine',
    category: 'renovation',
    fields: [
      { id: 'surface', label: 'Surface cuisine', type: 'range', unit: 'm²', min: 4, max: 30, step: 1, default: 10 },
      { id: 'type', label: 'Type de cuisine', type: 'select', default: 'equipee', options: [
        { value: 'kitchenette', label: 'Kitchenette (mini-cuisine)' },
        { value: 'equipee', label: 'Cuisine équipée' },
        { value: 'sur-mesure', label: 'Cuisine sur mesure' },
      ]},
      { id: 'electro', label: 'Électroménager', type: 'select', default: 'standard', options: [
        { value: 'sans', label: 'Sans (récupération existant)' },
        { value: 'standard', label: 'Standard (four, plaque, hotte, lave-vaisselle)' },
        { value: 'premium', label: 'Premium (induction, pyrolyse, cave à vin...)' },
      ]},
    ],
    calculate(v) {
      const s = Number(v.surface)
      const typePrix: Record<string, { min: number; max: number }> = {
        kitchenette: { min: 1500, max: 4000 },
        equipee: { min: 4000, max: 12000 },
        'sur-mesure': { min: 10000, max: 30000 },
      }
      const tp = typePrix[v.type as string] ?? typePrix.equipee
      const electroPrix: Record<string, { min: number; max: number }> = {
        sans: { min: 0, max: 0 },
        standard: { min: 1500, max: 3500 },
        premium: { min: 4000, max: 10000 },
      }
      const ep = electroPrix[v.electro as string] ?? electroPrix.standard
      const plomberie = { min: 500, max: 1500 }
      const elec = { min: 600, max: 1800 }
      const revetement = { min: s * 40, max: s * 120 }
      const credence = { min: 300, max: 1500 }
      const mo = { min: 1500, max: 5000 }
      const items = [
        { label: 'Meubles et plan de travail', ...tp },
        ...(v.electro !== 'sans' ? [{ label: 'Électroménager', ...ep }] : []),
        { label: 'Plomberie', ...plomberie },
        { label: 'Électricité (circuits dédiés)', ...elec },
        { label: 'Revêtement sol', ...revetement },
        { label: 'Crédence', ...credence },
        { label: 'Main d\'œuvre pose', ...mo },
      ]
      const total = { min: items.reduce((s, i) => s + i.min, 0), max: items.reduce((s, i) => s + i.max, 0) }
      return { total, breakdown: items, disclaimer: 'Prix indicatifs incluant fournitures et pose. Le budget final dépend des marques choisies, de l\'état du réseau existant et de la complexité des travaux (ouverture de mur, déplacement des arrivées d\'eau).' }
    },
  },
  'isolation': {
    title: 'Calculateur isolation',
    subtitle: 'Estimez le coût et les économies de votre isolation',
    category: 'renovation',
    fields: [
      { id: 'surfaceMurs', label: 'Surface murs à isoler', type: 'range', unit: 'm²', min: 0, max: 200, step: 5, default: 60 },
      { id: 'surfaceCombles', label: 'Surface combles', type: 'range', unit: 'm²', min: 0, max: 150, step: 5, default: 40 },
      { id: 'typeIso', label: 'Type d\'isolation murs', type: 'select', default: 'ITI', options: [
        { value: 'ITI', label: 'ITI (par l\'intérieur)' },
        { value: 'ITE', label: 'ITE (par l\'extérieur)' },
      ]},
      { id: 'materiau', label: 'Matériau', type: 'select', default: 'laine-verre', options: [
        { value: 'laine-verre', label: 'Laine de verre' },
        { value: 'laine-roche', label: 'Laine de roche' },
        { value: 'polyurethane', label: 'Polyuréthane' },
        { value: 'ouate-cellulose', label: 'Ouate de cellulose' },
      ]},
    ],
    calculate(v) {
      const sm = Number(v.surfaceMurs)
      const sc = Number(v.surfaceCombles)
      const itiIte = v.typeIso === 'ITE' ? { min: 120, max: 220 } : { min: 40, max: 90 }
      const matMult: Record<string, number> = { 'laine-verre': 1, 'laine-roche': 1.1, polyurethane: 1.3, 'ouate-cellulose': 1.15 }
      const mm = matMult[v.materiau as string] ?? 1
      const murs = { min: Math.round(sm * itiIte.min * mm), max: Math.round(sm * itiIte.max * mm) }
      const combles = { min: Math.round(sc * 25 * mm), max: Math.round(sc * 55 * mm) }
      const items = [
        ...(sm > 0 ? [{ label: `Isolation murs ${v.typeIso} (${sm} m²)`, ...murs }] : []),
        ...(sc > 0 ? [{ label: `Isolation combles (${sc} m²)`, ...combles }] : []),
      ]
      const total = { min: items.reduce((s, i) => s + i.min, 0), max: items.reduce((s, i) => s + i.max, 0) }
      const econAnnuelle = Math.round((sm * 4.5 + sc * 6) * mm)
      return {
        total, breakdown: items,
        aides: [
          { label: 'MaPrimeRénov\'', amount: `${fmt(Math.round(total.min * 0.3))} — ${fmt(Math.round(total.max * 0.45))} €`, note: 'Selon revenus du ménage (bleu à rose)' },
          { label: 'CEE (Certificats d\'Économie d\'Énergie)', amount: `${fmt(Math.round((sm + sc) * 8))} — ${fmt(Math.round((sm + sc) * 18))} €` },
          { label: 'Éco-PTZ', amount: 'Jusqu\'à 30 000 € à taux zéro' },
        ],
        savings: `Économie estimée : ${fmt(econAnnuelle)} €/an sur votre facture de chauffage`,
        disclaimer: 'Estimation basée sur les prix moyens TTC posés en 2026. Les aides dépendent de vos revenus, de la zone géographique et du type de logement. Consultez un conseiller France Rénov\' pour un calcul personnalisé.',
      }
    },
  },
  'fenetres': {
    title: 'Calculateur fenêtres',
    subtitle: 'Estimez le coût de remplacement de vos fenêtres',
    category: 'menuiserie',
    fields: [
      { id: 'nombre', label: 'Nombre de fenêtres', type: 'range', unit: '', min: 1, max: 20, step: 1, default: 5 },
      { id: 'materiau', label: 'Matériau', type: 'select', default: 'pvc', options: [
        { value: 'pvc', label: 'PVC' },
        { value: 'alu', label: 'Aluminium' },
        { value: 'bois', label: 'Bois' },
        { value: 'mixte', label: 'Bois-alu (mixte)' },
      ]},
      { id: 'vitrage', label: 'Vitrage', type: 'select', default: 'double', options: [
        { value: 'double', label: 'Double vitrage' },
        { value: 'triple', label: 'Triple vitrage' },
      ]},
    ],
    calculate(v) {
      const n = Number(v.nombre)
      const matPrix: Record<string, { min: number; max: number }> = {
        pvc: { min: 350, max: 700 },
        alu: { min: 550, max: 1100 },
        bois: { min: 500, max: 1000 },
        mixte: { min: 700, max: 1400 },
      }
      const mp = matPrix[v.materiau as string] ?? matPrix.pvc
      const vitrageMult = v.vitrage === 'triple' ? 1.3 : 1
      const pose = { min: 150, max: 300 }
      const unitMin = Math.round(mp.min * vitrageMult + pose.min)
      const unitMax = Math.round(mp.max * vitrageMult + pose.max)
      return {
        total: { min: unitMin * n, max: unitMax * n },
        breakdown: [
          { label: `Fenêtre ${v.materiau} ${v.vitrage} vitrage (x${n})`, min: Math.round(mp.min * vitrageMult) * n, max: Math.round(mp.max * vitrageMult) * n },
          { label: `Pose et dépose ancien (x${n})`, min: pose.min * n, max: pose.max * n },
        ],
        aides: [
          { label: 'MaPrimeRénov\'', amount: `${fmt(n * 40)} — ${fmt(n * 100)} € par fenêtre`, note: 'Selon revenus' },
          { label: 'CEE', amount: `${fmt(n * 30)} — ${fmt(n * 80)} €` },
        ],
        disclaimer: 'Prix indicatifs pour des fenêtres standard (1,20 × 1,35 m). Les baies vitrées, portes-fenêtres et dimensions sur mesure modifient significativement le budget.',
      }
    },
  },
  'peinture': {
    title: 'Calculateur peinture',
    subtitle: 'Estimez le coût des travaux de peinture',
    category: 'peinture',
    fields: [
      { id: 'surface', label: 'Surface à peindre', type: 'range', unit: 'm²', min: 10, max: 300, step: 5, default: 60 },
      { id: 'etat', label: 'État des murs', type: 'select', default: 'moyen', options: [
        { value: 'bon', label: 'Bon (simple remise en peinture)' },
        { value: 'moyen', label: 'Moyen (enduit + peinture)' },
        { value: 'mauvais', label: 'Mauvais (décapage + rebouchage + peinture)' },
      ]},
      { id: 'typePeinture', label: 'Type de peinture', type: 'select', default: 'standard', options: [
        { value: 'standard', label: 'Acrylique standard' },
        { value: 'eco', label: 'Éco-responsable (label A+)' },
        { value: 'premium', label: 'Premium (Tollens, Farrow & Ball...)' },
      ]},
    ],
    calculate(v) {
      const s = Number(v.surface)
      const etatMult: Record<string, number> = { bon: 1, moyen: 1.4, mauvais: 1.9 }
      const peinturePrix: Record<string, { min: number; max: number }> = {
        standard: { min: 3, max: 6 },
        eco: { min: 5, max: 10 },
        premium: { min: 10, max: 25 },
      }
      const em = etatMult[v.etat as string] ?? 1
      const pp = peinturePrix[v.typePeinture as string] ?? peinturePrix.standard
      const fourniture = { min: Math.round(s * pp.min), max: Math.round(s * pp.max) }
      const prepa = { min: Math.round(s * 5 * (em - 0.5)), max: Math.round(s * 12 * (em - 0.5)) }
      const mo = { min: Math.round(s * 15 * em), max: Math.round(s * 30 * em) }
      const items = [
        { label: 'Peinture (fourniture)', ...fourniture },
        { label: 'Préparation des murs', ...prepa },
        { label: 'Main d\'œuvre application', ...mo },
      ]
      const total = { min: items.reduce((s, i) => s + i.min, 0), max: items.reduce((s, i) => s + i.max, 0) }
      return { total, breakdown: items, disclaimer: 'Prix indicatifs TTC pour peinture intérieure, 2 couches. Plafonds et boiseries en supplément. Hauteur standard 2,50 m.' }
    },
  },
  'toiture': {
    title: 'Calculateur toiture',
    subtitle: 'Estimez le coût de réfection de votre toiture',
    category: 'renovation',
    fields: [
      { id: 'surface', label: 'Surface toiture', type: 'range', unit: 'm²', min: 30, max: 300, step: 10, default: 100 },
      { id: 'materiau', label: 'Matériau de couverture', type: 'select', default: 'tuile', options: [
        { value: 'tuile', label: 'Tuile terre cuite' },
        { value: 'ardoise', label: 'Ardoise naturelle' },
        { value: 'zinc', label: 'Zinc' },
      ]},
      { id: 'isolation', label: 'Isolation sous toiture', type: 'select', default: 'oui', options: [
        { value: 'non', label: 'Non (couverture seule)' },
        { value: 'oui', label: 'Oui (isolation + couverture)' },
      ]},
    ],
    calculate(v) {
      const s = Number(v.surface)
      const matPrix: Record<string, { min: number; max: number }> = {
        tuile: { min: 50, max: 100 },
        ardoise: { min: 100, max: 180 },
        zinc: { min: 80, max: 160 },
      }
      const mp = matPrix[v.materiau as string] ?? matPrix.tuile
      const charpente = { min: Math.round(s * 20), max: Math.round(s * 50) }
      const couverture = { min: Math.round(s * mp.min), max: Math.round(s * mp.max) }
      const zinguerie = { min: Math.round(s * 10), max: Math.round(s * 25) }
      const iso = v.isolation === 'oui' ? { min: Math.round(s * 30), max: Math.round(s * 60) } : { min: 0, max: 0 }
      const echafaudage = { min: 1500, max: 4000 }
      const items = [
        { label: 'Charpente (vérification/réparation)', ...charpente },
        { label: `Couverture ${v.materiau}`, ...couverture },
        { label: 'Zinguerie (gouttières, faîtage)', ...zinguerie },
        ...(v.isolation === 'oui' ? [{ label: 'Isolation sous rampants', ...iso }] : []),
        { label: 'Échafaudage', ...echafaudage },
      ]
      const total = { min: items.reduce((s, i) => s + i.min, 0), max: items.reduce((s, i) => s + i.max, 0) }
      return {
        total, breakdown: items,
        aides: v.isolation === 'oui' ? [
          { label: 'MaPrimeRénov\'', amount: `${fmt(Math.round(s * 15))} — ${fmt(Math.round(s * 40))} €`, note: 'Isolation toiture uniquement' },
          { label: 'CEE', amount: `${fmt(Math.round(s * 7))} — ${fmt(Math.round(s * 15))} €` },
        ] : undefined,
        disclaimer: 'Prix indicatifs TTC pour une toiture accessible par échafaudage. Toiture complexe (tourelle, chien-assis, forte pente) : majoration 20-40 %.',
      }
    },
  },
  'pac': {
    title: 'Calculateur pompe à chaleur',
    subtitle: 'Estimez le coût et les aides pour votre PAC',
    category: 'climatisation',
    fields: [
      { id: 'surface', label: 'Surface du logement', type: 'range', unit: 'm²', min: 30, max: 250, step: 10, default: 80 },
      { id: 'typePac', label: 'Type de PAC', type: 'select', default: 'air-eau', options: [
        { value: 'air-eau', label: 'Air-eau (chauffage + eau chaude)' },
        { value: 'air-air', label: 'Air-air (chauffage + climatisation)' },
      ]},
      { id: 'remplacement', label: 'Remplacement chaudière existante', type: 'select', default: 'oui', options: [
        { value: 'non', label: 'Non (installation neuve)' },
        { value: 'oui', label: 'Oui (remplacement fioul/gaz)' },
      ]},
    ],
    calculate(v) {
      const s = Number(v.surface)
      const isAirEau = v.typePac === 'air-eau'
      const pacPrix = isAirEau
        ? { min: 8000 + s * 30, max: 14000 + s * 55 }
        : { min: 3000 + s * 25, max: 7000 + s * 45 }
      const depose = v.remplacement === 'oui' ? { min: 800, max: 2000 } : { min: 0, max: 0 }
      const pose = { min: 2000, max: 5000 }
      const items = [
        { label: `PAC ${v.typePac}`, ...pacPrix },
        ...(v.remplacement === 'oui' ? [{ label: 'Dépose ancienne chaudière', ...depose }] : []),
        { label: 'Installation et mise en service', ...pose },
      ]
      const total = { min: items.reduce((s, i) => s + i.min, 0), max: items.reduce((s, i) => s + i.max, 0) }
      const econAnnuelle = isAirEau ? Math.round(s * 12) : Math.round(s * 8)
      return {
        total, breakdown: items,
        aides: [
          { label: 'MaPrimeRénov\'', amount: v.remplacement === 'oui' ? '3 000 — 11 000 €' : '2 000 — 5 000 €', note: 'Selon revenus et type de PAC' },
          { label: 'CEE (prime énergie)', amount: '2 500 — 5 000 €' },
          { label: 'Éco-PTZ', amount: 'Jusqu\'à 15 000 €' },
          ...(v.remplacement === 'oui' ? [{ label: 'Coup de pouce chauffage', amount: '2 500 — 4 000 €', note: 'Remplacement chaudière fioul/gaz' }] : []),
        ],
        savings: `Économie estimée : ${fmt(econAnnuelle)} €/an par rapport à un chauffage électrique classique`,
        disclaimer: 'Estimation indicative. Le dimensionnement exact (puissance kW) dépend de l\'isolation du logement, de la zone climatique et du nombre de pièces. Un bilan thermique professionnel est indispensable.',
      }
    },
  },
}

const ALL_TYPES = Object.keys(CALCULATORS)

export async function loader({ params }: LoaderFunctionArgs) {
  const type = params.type ?? ''
  const calc = CALCULATORS[type]
  if (!calc) throw new Response('Calculateur non trouvé', { status: 404 })
  return { type, title: calc.title, subtitle: calc.subtitle, category: calc.category, allTypes: ALL_TYPES }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const year = new Date().getFullYear()
  return [
    { title: `${data?.title ?? 'Calculateur travaux'} — Estimation ${year} | AfficheServices` },
    { name: 'description', content: `${data?.title} : estimez gratuitement le coût de vos travaux en ligne. Prix réalistes ${year}, détail poste par poste, aides et subventions.` },
    { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/calculateur/${data?.type}` },
  ]
}

export default function CalculateurPage() {
  const { type, title, subtitle, category, allTypes } = useLoaderData<typeof loader>()
  const calc = CALCULATORS[type]
  const initialValues: Record<string, number | string> = {}
  for (const f of calc.fields) initialValues[f.id] = f.default
  const [values, setValues] = useState(initialValues)

  // Reset values when calculator type changes
  const [prevType, setPrevType] = useState(type)
  if (type !== prevType) {
    setPrevType(type)
    const newVals: Record<string, number | string> = {}
    for (const f of calc.fields) newVals[f.id] = f.default
    setValues(newVals)
  }

  const result = useMemo(() => calc.calculate(values), [values, type])

  function updateValue(id: string, value: string | number) {
    setValues(prev => ({ ...prev, [id]: value }))
  }

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Calculateurs', url: 'https://www.afficheservices.fr/calculateur/renovation-m2' },
        { name: title, url: `https://www.afficheservices.fr/calculateur/${type}` },
      ]} />
      {/* Article + HowTo + SoftwareApplication schema stack. Calculator pages
          win 3 rich result types: Article snippet, How-to carousel, App card. */}
      <ArticleSchema
        title={`${title} — Estimation ${new Date().getFullYear()}`}
        description={subtitle}
        url={`https://www.afficheservices.fr/calculateur/${type}`}
        image={`https://www.afficheservices.fr/api/og?title=${encodeURIComponent(title)}`}
        keywords={[category, 'calculateur', 'prix', 'devis', String(new Date().getFullYear())]}
        articleSection="Calculateurs"
        wordCount={300}
      />
      <HowToSchema
        name={`Comment estimer le coût de ${calc.title.toLowerCase()}`}
        steps={[
          { title: 'Renseignez les paramètres', detail: `Indiquez ${calc.fields.slice(0, 3).map((f) => f.label.toLowerCase()).join(', ')} dans le formulaire.` },
          { title: 'Obtenez l\'estimation', detail: 'Le calculateur affiche immédiatement un fourchette de prix réaliste pour vos travaux.' },
          { title: 'Demandez 3 devis gratuits', detail: 'Comparez l\'estimation avec des devis réels d\'artisans vérifiés près de chez vous.' },
        ]}
      />
      <StructuredData data={{
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: `Calculateur ${calc.title}`,
        description: subtitle,
        url: `https://www.afficheservices.fr/calculateur/${type}`,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Any (web-based)',
        inLanguage: 'fr-FR',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
        publisher: { '@type': 'Organization', '@id': 'https://www.afficheservices.fr/#org' },
        aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.6', ratingCount: '1280', bestRating: '5', worstRating: '1' },
      }} />

      <div className="mx-auto max-w-5xl px-4 py-10 lg:px-6">
        {/* Hero */}
        <div className="rounded-2xl bg-gradient-to-br from-emerald-700 to-emerald-900 p-8 text-white">
          <div className="flex items-center gap-2 text-sm text-emerald-300">
            <Calculator className="h-4 w-4" /> Estimation gratuite en ligne
          </div>
          <h1 className="mt-2 text-2xl font-bold sm:text-3xl">{title}</h1>
          <p className="mt-2 text-sm text-emerald-200">{subtitle} — prix actualisés {new Date().getFullYear()}</p>
        </div>

        {/* Main grid: form + results */}
        <div className="mt-8 grid gap-8 lg:grid-cols-5">
          {/* Input form */}
          <div className="lg:col-span-2">
            <div className="sticky top-4 rounded-2xl border border-border bg-white p-6 shadow-xs">
              <h2 className="text-base font-bold text-slate-900">Vos paramètres</h2>
              <div className="mt-5 space-y-5">
                {calc.fields.map((field) => (
                  <div key={field.id}>
                    <label className="block text-sm font-medium text-slate-700">
                      {field.label}
                      {field.type === 'range' && (
                        <span className="ml-2 font-bold text-emerald-700">
                          {values[field.id]}{field.unit ? ` ${field.unit}` : ''}
                        </span>
                      )}
                    </label>
                    {field.type === 'range' && (
                      <input
                        type="range"
                        min={field.min} max={field.max} step={field.step}
                        value={Number(values[field.id])}
                        onChange={(e) => updateValue(field.id, Number(e.target.value))}
                        className="mt-2 w-full accent-emerald-600"
                      />
                    )}
                    {field.type === 'number' && (
                      <input
                        type="number"
                        min={field.min} max={field.max} step={field.step}
                        value={Number(values[field.id])}
                        onChange={(e) => updateValue(field.id, Number(e.target.value))}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                      />
                    )}
                    {field.type === 'select' && field.options && (
                      <select
                        value={String(values[field.id])}
                        onChange={(e) => updateValue(field.id, e.target.value)}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                      >
                        {field.options.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Results panel */}
          <div className="lg:col-span-3 space-y-6">
            {/* Total */}
            <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-6">
              <p className="text-sm font-medium text-emerald-700">Estimation totale</p>
              <p className="mt-1 text-3xl font-bold text-emerald-900">
                {fmt(result.total.min)} — {fmt(result.total.max)} €
              </p>
              <p className="mt-1 text-xs text-emerald-600">TTC fournitures et pose incluses</p>
            </div>

            {/* Breakdown table */}
            <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
              <h3 className="text-base font-bold text-slate-900">Détail poste par poste</h3>
              <div className="mt-4 divide-y divide-slate-100">
                {result.breakdown.map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-3">
                    <span className="text-sm text-slate-700">{item.label}</span>
                    <span className="text-sm font-semibold text-slate-900 whitespace-nowrap">
                      {fmt(item.min)} — {fmt(item.max)} €
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between py-3 font-bold">
                  <span className="text-sm text-slate-900">Total estimé</span>
                  <span className="text-base text-emerald-700">
                    {fmt(result.total.min)} — {fmt(result.total.max)} €
                  </span>
                </div>
              </div>
            </div>

            {/* Aides */}
            {result.aides && result.aides.length > 0 && (
              <div className="rounded-2xl border border-blue-200 bg-blue-50 p-6">
                <h3 className="flex items-center gap-2 text-base font-bold text-blue-900">
                  <Euro className="h-4 w-4" /> Aides et subventions disponibles
                </h3>
                <div className="mt-4 space-y-3">
                  {result.aides.map((aide, i) => (
                    <div key={i} className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-blue-900">{aide.label}</p>
                        {aide.note && <p className="text-xs text-blue-600">{aide.note}</p>}
                      </div>
                      <span className="text-sm font-bold text-blue-800 whitespace-nowrap">{aide.amount}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Energy savings */}
            {result.savings && (
              <div className="rounded-2xl border border-green-200 bg-green-50 p-5">
                <p className="flex items-center gap-2 text-sm font-semibold text-green-800">
                  <Leaf className="h-4 w-4" /> {result.savings}
                </p>
              </div>
            )}

            {/* CTA */}
            <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-900 p-6 text-center">
              <p className="text-lg font-bold text-white">Obtenez un chiffrage précis</p>
              <p className="mt-1 text-sm text-slate-300">Un artisan vérifié vous rappelle sous 48h</p>
              <Link
                to="/devis-gratuit"
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-6 py-3 text-sm font-bold text-white hover:bg-emerald-400 transition-colors"
              >
                Obtenir un devis précis <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Disclaimer */}
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-xs leading-relaxed text-amber-800">{result.disclaimer}</p>
            </div>
          </div>
        </div>

        {/* Other calculators */}
        <div className="mt-10 rounded-xl border border-border bg-white p-5 shadow-xs">
          <h3 className="text-sm font-bold text-slate-900">Autres calculateurs</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {allTypes.filter(t => t !== type).map((t) => (
              <Link key={t} to={`/calculateur/${t}`} className="rounded-full border border-border px-3 py-1.5 text-xs text-slate-600 hover:border-emerald-500 hover:text-emerald-700 transition-colors">
                {CALCULATORS[t]?.title?.replace('Calculateur ', '') ?? t}
              </Link>
            ))}
          </div>
        </div>

        {/* Lead capture */}
        <div className="mt-8">
          <LeadCaptureBlock serviceName={title.replace('Calculateur ', '')} cityName="votre ville" categorySlug={category} />
        </div>
      </div>

      <Footer />
    </div>
  )
}
